/**
 * Provider string stored in Omega's credential row. The generic
 * `/nodes/{provider}/credentials` route matches on this column value, so it is
 * the spelling to send there — `jotform` would come back as an empty array,
 * which is indistinguishable from "this org has no credential".
 *
 * The JotForm-specific routes use the literal segment `jotform` instead. Both
 * spellings are correct; they address different routes.
 */
export const JOTFORM_STORED_PROVIDER = "jotform_submission";

/**
 * Hidden field the customer adds to their JotForm. Its value is the issued
 * shared secret, which is how a submission authenticates itself.
 */
export const JOTFORM_HIDDEN_FIELD_NAME = "x_jotform_credential";

/** Pin state of the org's JotForm credential. Empty `formIds` means accept-any-form. */
export type JotFormPinnedForms = {
  id: string;
  formIds: string[];
  /** Last change of any kind — a pin write moves it too, so it is not a last-rotated stamp. */
  updatedAt: string;
};

/** Issue/rotate response. `secret` is returned exactly once and never again. */
export type JotFormIssuedSecret = JotFormPinnedForms & { secret: string };

export type JotFormWebhookTarget = {
  workflowId: string;
  workflowName: string;
  url: string;
  /**
   * Omega's `isLive` — `webhook.isEnabled` on the Start node, reported rather
   * than filtered on. False means the workflow is marked but its webhook is off,
   * so submissions to this URL are rejected `workflow_not_triggerable`. The row
   * is listed anyway, flagged, because a silently-absent row sends the customer
   * to reconfigure a workflow that is already marked correctly.
   */
  live: boolean;
};

/**
 * `truncated` is derived, not read: `GET /nodes/jotform/workflows` caps at 100
 * rows and reports the cut only as a span tag, so the client mirrors Omega's own
 * exactly-at-cap-reads-as-truncated rule.
 */
export type JotFormWebhookTargets = {
  targets: JotFormWebhookTarget[];
  truncated: boolean;
};

/** `status` is set on failure so the caller can map it to a translation key. */
export type JotFormActionResult<T> = { data?: T; status?: number };

/**
 * How much the API key can do, in Omega's own spelling: `read_only`, not
 * `readonly`. `unknown` means there was no form to probe against, which is
 * normal for an empty JotForm account, not a failure.
 */
export type JotFormAccountScope = "full" | "read_only" | "unknown";

/** The JotForm account an API key belongs to, as echoed back by Omega. */
export type JotFormAccount = {
  username: string;
  name: string;
  email: string;
  /** JotForm's plan name, URL prefix already stripped by Omega. e.g. "FREE". */
  accountType: string;
  /** e.g. "ACTIVE". */
  status: string;
};

/** 200 body of `GET /nodes/jotform/account?secretName=<name>`. */
export type JotFormAccountResponse = {
  account: JotFormAccount;
  scope: JotFormAccountScope;
  /** A sentence, not an object. Populated only when `scope` is "read_only". */
  blocker: string | null;
};

/**
 * The outcome of one account check. A union, because a read-only key answers
 * 200 with a full account *and* a blocker. Every failure member carries
 * Omega's `detail` sentence, since the wire gives no machine-readable code and
 * the branch is on HTTP status alone.
 */
export type JotFormAccountResult =
  | { kind: "ok"; account: JotFormAccount; scope: JotFormAccountScope }
  | { kind: "blocked"; account: JotFormAccount; blocker: string }
  | { kind: "unusableKey"; detail: string }
  | { kind: "invalidKey"; detail: string }
  | { kind: "refused"; detail: string }
  | { kind: "notFound" }
  | { kind: "rateLimited"; retryAfterSeconds: number | null }
  | { kind: "upstream"; detail: string };

/* ------------------------------------------------------------------------- *
 * Connect branch: reading a customer's own JotForm account, and writing the
 * connection into it. Every call takes the *name* of the workflow secret
 * holding their API key, never the key.
 * ------------------------------------------------------------------------- */

/**
 * The taxonomy every connect route answers with. No machine-readable code on
 * the wire, so the branch is on status and each failure carries Omega's
 * `detail` sentence to render verbatim.
 */
export type JotFormCallFailure =
  /** 400. A malformed id, or a stored value JotForm will not accept as a key. */
  | { kind: "badRequest"; detail: string }
  /** 401. JotForm rejected the key. */
  | { kind: "invalidKey"; detail: string }
  /** 403. JotForm refusing this key — not Gail refusing the operator. */
  | { kind: "refused"; detail: string }
  /** 404. The secret, the form or the workflow is gone. */
  | { kind: "notFound"; detail: string }
  /** 409. `expectedRevision` is stale — the workflow was republished. */
  | { kind: "conflict"; detail: string }
  /** 422. A read-only key, or a duplicated credential field naming its qids. */
  | { kind: "unprocessable"; detail: string }
  | { kind: "rateLimited"; retryAfterSeconds: number | null }
  /**
   * 502 and anything else. A 502 from apply may be a false alarm: apply verifies
   * by re-reading the form, and JotForm's read-back lags its own writes.
   */
  | { kind: "upstream"; detail: string };

export type JotFormCallResult<T> = { kind: "ok"; data: T } | JotFormCallFailure;

/** `createdAt`/`updatedAt` are zoneless local times: sort and format, never arithmetic. */
export type JotFormForm = {
  id: string;
  title: string;
  /** Always "ENABLED" — Omega filters the rest out. */
  status: string;
  submissionCount: number;
  createdAt: string;
  updatedAt: string;
};

/** `forms` is enabled only and capped; `total` is JotForm's own count. */
export type JotFormFormList = {
  forms: JotFormForm[];
  total: number;
};

/** Omega's row cap on `GET /nodes/jotform/forms`. */
export const JOTFORM_FORMS_CAP = 1000;

/**
 * JSON Schema fragment for one question's value. A composite is an object with
 * subkeys; a scalar is always a string, `control_number` included.
 */
export type JotFormQuestionShape = {
  type?: string;
  properties?: Record<string, JotFormQuestionShape>;
  required?: string[];
};

export type JotFormQuestion = {
  qid: string;
  /** JotForm's own control type, e.g. `control_fullname`, `control_dropdown`. */
  type: string;
  label: string;
  uniqueName: string;
  order: number;
  /** False for a field that answers nothing (a header, a page break). */
  answers: boolean;
  hidden: boolean;
  /** Not always a boolean. Read through `isJotFormQuestionRequired`, never `=== true`. */
  required: boolean | string;
  /**
   * The normalized payload key the ingress will produce. `null` when the field
   * answers nothing. Two fields normalizing onto one key both report it.
   */
  key: string | null;
  shapeKnown: boolean;
  shape: JotFormQuestionShape | null;
};

export type JotFormQuestions = {
  formId: string;
  questions: JotFormQuestion[];
};

/**
 * Anything but an explicit no counts as required, the safe direction:
 * over-reporting only costs a guard that was not needed.
 */
export function isJotFormQuestionRequired(
  question: Pick<JotFormQuestion, "required">
): boolean {
  const { required } = question;
  if (typeof required === "boolean") return required;
  const answer = required?.trim().toLowerCase();
  return Boolean(answer) && answer !== "no" && answer !== "false";
}

/** Whose hook a row is. Every Omega hook reads `other_workflow` without a `workflowId`. */
export type JotFormWebhookKind = "this_workflow" | "other_workflow" | "foreign";

export type JotFormFormWebhook = {
  index: number;
  url: string;
  kind: JotFormWebhookKind;
  workflowId: string | null;
  knownWorkflow: boolean | null;
};

export type JotFormFormWebhooks = {
  formId: string;
  webhooks: JotFormFormWebhook[];
};

/** One Unique Name Omega will rewrite, to stop two fields colliding on one key. */
export type JotFormUniqueNameChange = {
  qid: string;
  from: string;
  to: string;
  keyBefore: string;
  keyAfter: string;
};

/**
 * A gap between the workflow's input schema and what the form produces. Only
 * `missingRequired` is acted on, aborting before any write; the rest are
 * informational (omega `docs/features/jotform-connect-preview.md`).
 */
export type JotFormConnectGap = {
  key?: string;
  detail?: string;
};

/** `POST .../connect/preview`. Verification after publish, never the decision point. */
export type JotFormConnectPreview = {
  form: { id: string; title?: string };
  workflow: { id: string; revision: number; isLive: boolean };
  uniqueNameChanges: JotFormUniqueNameChange[];
  credentialField: { outcome?: string; qid?: string } | null;
  webhooks: JotFormFormWebhook[];
  producedSchema: Record<string, unknown> | null;
  gaps: {
    missingRequired: JotFormConnectGap[];
    shapeMismatches: JotFormConnectGap[];
    unread: JotFormConnectGap[];
  };
  warnings: string[];
};

/** A webhook apply left alone. Nothing is ever deleted. */
export type JotFormApplyWebhookRef = {
  url?: string;
  workflowId?: string | null;
};

/**
 * `POST .../connect`. Renames, credential field, verify, pin, then the webhook
 * last, so a run that stops part-way leaves the form behaving as before.
 */
export type JotFormConnectApply = {
  uniqueNames: {
    outcome: "applied" | "unchanged";
    renamed: JotFormUniqueNameChange[];
  };
  credentialField: {
    outcome: "added" | "updated" | "hidden" | "unchanged";
  };
  /** `not_pinned` is not `unchanged`: the credential accepts any form, and says so. */
  pin: { outcome: "appended" | "unchanged" | "not_pinned" };
  webhook: {
    outcome: "added" | "unchanged";
    otherWorkflows: JotFormApplyWebhookRef[];
    foreign: JotFormApplyWebhookRef[];
  };
  /** Always true in a 200; a failed verification is a 502. */
  verified: boolean;
  warnings: string[];
};
